import { useState } from "react";
import "./OnlineBooking.css";
import { FaStripe } from "react-icons/fa";
import { IoLockClosed } from "react-icons/io5";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import MyAlert from '../../Components/Reusable/Alert/Alert';
import {
  ConnectStripeAccount,
  UpdateAcceptOnlinePaymentsOption,
  GetLoginLink,
  DisconnectStripeAccount,
  UpdateCurrency,
  RefreshBalances,
  ConnectStripeAccountAttention
} from './Requests';
import { formatMinor } from "../../Components/Reusable/CurrencyFormatter/CurrencyFormatter";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import {
  notifyError,
  notifySuccess,
  notifyInfo
} from '../../Response/ResposeHandler';
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import { useTranslation } from "react-i18next";

const currencies = [
  { value: 0, label: 'GBP' },
  { value: 1, label: 'EUR' }
];

const Payments = (props) => {
  const { t } = useTranslation("payments");
  const { t: a } = useTranslation("miniSiteGeneral");
  const navigate = useNavigate();

  const config = props.config;
  const paymentServiceDetails = props.paymentServiceDetails;
  const hasOnlinePaymentsAccess = !!props.config?.features?.onlinePayments;
  const hasConnectedStripeAccount = !!config.stripeAccountId;

  const [connectStripeAccountLoading, setConnectStripeAccountLoading] = useState(false);
  const [status] = useState(props.status);
  const [enabledOnlinePaymentsButton, setEnableOnlinePaymentsButtons] = useState(props.config.acceptOnlinePayments);
  const [manageAccountButtonLoading, setManageAccountButtonLoading] = useState(false);
  const [disconnectAccountButtonLoading, setDisconnectAccountButtonLoading] = useState(false);
  const [currency, setCurrency] = useState(currencies.find(x => x.label === props.config.currency));
  const [currencyButtonLoading, setCurrencyButtonLoading] = useState(false);

  const bookingUrl = `${process.env.REACT_APP_CLIENT_BASE_URL}make-a-booking?id=${props.config.barberShopId}`;

  const LockedBadge = () => (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="stripe-payments-locked-tooltip">
          {t('tooltips.locked')}
        </Tooltip>
      }
    >
      <div className="payLockedBadge" aria-label={t('tooltips.locked')}>
        <IoLockClosed />
      </div>
    </OverlayTrigger>
  );

  function ConnectStripAccountButtonClick() {
    if (!hasOnlinePaymentsAccess) {
      notifyError(t('toasts.onlinePaymentsLocked'));
      return;
    }

    setConnectStripeAccountLoading(true);
    ConnectStripeAccount()
      .then((res) => {
        if (res.status === 200) {
          window.open(res.data);
        }
      })
      .finally(() => {
        setConnectStripeAccountLoading(false);
      });
  }

  function ConnectStripAccountAttentionButtonClick() {
    if (!hasOnlinePaymentsAccess) {
      notifyError(t('toasts.onlinePaymentsLocked'));
      return;
    }

    setConnectStripeAccountLoading(true);
    ConnectStripeAccountAttention()
      .then((res) => {
        if (res.status === 200) {
          window.open(res.data);
        }
      })
      .finally(() => {
        setConnectStripeAccountLoading(false);
      });
  }

  function updateAcceptPaymentsOption(e) {
    if (!hasOnlinePaymentsAccess) {
      notifyError(t('toasts.onlinePaymentsLocked'));
      return;
    }

    const checked = e.target.checked;
    UpdateAcceptOnlinePaymentsOption(checked)
      .then((res) => {
        if (res.status === 200 && checked) {
          notifySuccess(t('toasts.onlinePaymentsOn'));
          setEnableOnlinePaymentsButtons(true);
        }
        if (res.status === 200 && !checked) {
          notifySuccess(t('toasts.onlinePaymentsOff'));
          setEnableOnlinePaymentsButtons(false);
        }
      })
      .catch(() => {
        notifyError(t('toasts.onlinePaymentsToggleError'));
        setEnableOnlinePaymentsButtons(false);
      });
  }

  function SubmitCurrency() {
    setCurrencyButtonLoading(true);
    UpdateCurrency(currency.label)
      .then((res) => {
        if (res.status === 200) {
          notifySuccess(t('toasts.currencySaved'));
        }
      })
      .catch(() => {
        notifyError(t('toasts.currencySaveError'));
      })
      .finally(() => {
        setCurrencyButtonLoading(false);
        props.getConfig();
      });
  }

  function ManageStripeAccountClicked() {
    setManageAccountButtonLoading(true);
    GetLoginLink()
      .then((res) => {
        if (res.status === 200) {
          window.open(res.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        setManageAccountButtonLoading(false);
      });
  }

  function DisconnectAccountClicked() {
    setDisconnectAccountButtonLoading(true);
    MyAlert.fire({
      title: t('alerts.confirmDisconnect.title'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--primary)',
      cancelButtonColor: '#aaa',
      confirmButtonText: t('alerts.confirmDisconnect.confirmButton')
    }).then((result) => {
      if (result.isConfirmed) {
        props.setLoading(true)
        DisconnectStripeAccount()
          .then((res) => {
            if (res.status === 200) {
              props.getConfig();
              notifySuccess(t('toasts.stripeDisconnected'));
              // navigate(0);
            }
          })
          .catch(() => {
            notifyError(t('toasts.stripeDisconnectError'));
          })
          .finally(() => {
            setDisconnectAccountButtonLoading(false);
            props.setLoading(false)
          });
      } else {
        setDisconnectAccountButtonLoading(false);
        props.setLoading(false)
      }
    });
  }

  function RefreshBalanceWrapper() {
    RefreshBalances()
      .then((res) => {
        if (res.status === 200) {
          notifySuccess(t('toasts.balanceUpdated'));
        }
      })
      .catch(() => {
        notifyError(t('toasts.balanceUpdateError'));
      })
      .finally(() => {
        props.getConfig();
      });
  }

  const statusText = (() => {
    if (status === "Connected") return t('status.connectedToStripe');
    if (status === "no payouts") return t('status.payoutsNotSetUp');
    if (status === "no charges") return t('status.chargesNotSetUp');
    return '';
  })();

  return (
    <>
    {props.ShowBookingPortalLink ?
      <div className="mini-card-general gen-card">
        <div className="gen-cardHead">
          <h3 className="gen-h3">{a("titles.bookingPortal")}</h3>
          <p className="gen-muted">{a("descriptions.bookingPortal")}</p>
        </div>

        <div className="mini-linkRow">
          <a
            target="_blank"
            rel="noreferrer"
            className="mini-link"
            href={`make-a-booking?id=${props.config.barberShopId}`}
            title={a("actions.openBookingPortal")}
            aria-label={a("actions.openBookingPortal")}
          >
            {bookingUrl}
          </a>

          <button
            type="button"
            className="mini-copyBtn"
            aria-label={a("actions.copyBookingLink")}
            onClick={async () => {
              notifyInfo(t("toasts.copiedBookingLink"));
              try {
                await navigator.clipboard.writeText(bookingUrl);
              } catch {}
            }}
            title={a("actions.copyLink")}
          >
            <svg viewBox="0 0 24 24" className="mini-copyIcon" aria-hidden="true">
              <path d="M16 1H6a2 2 0 0 0-2 2v11h2V3h10V1zm3 4H10a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H10V7h9v14z" />
            </svg>
          </button>
        </div>
      </div>
      :
      null}

  {props.ShowPaymentPanel ?
      <>
      {!hasConnectedStripeAccount ? (
        <div>
          <div className={`payConnect ${!hasOnlinePaymentsAccess ? 'paySectionLocked' : ''}`}>
            <div className="payConnect__card">
              {!hasOnlinePaymentsAccess && <LockedBadge />}

              <div className="payConnect__info">
                <div className="payConnect__badge">{t('badge.payments')}</div>
                <h3 className="payConnect__title">{t('titles.connectStripe')}</h3>
                <p className="payConnect__sub">
                  {t('subtitles.enableOnlinePayments')}
                </p>
              </div>

              <button
                type="button"
                className="payConnect__btn"
                onClick={ConnectStripAccountButtonClick}
                disabled={connectStripeAccountLoading || !hasOnlinePaymentsAccess}
              >
                <span className="payConnect__btnIcon" aria-hidden="true">
                  <FaStripe />
                </span>
                {!connectStripeAccountLoading ? (
                  <span>{t('actions.connectStripeAccount')}</span>
                ) : (
                  <span className="app-btn__spinner" />
                )}
              </button>
            </div>
          </div>

          <section className="schedCard mt-4">
            <div className="gen-cardHead">
              <h3 className="gen-h3">{t('titles.currency')}</h3>
              <p className="gen-muted">{t('subtitles.currencyDescription')}</p>
            </div>

            <div className="schedBody">
              <div className="schedSelect">
                <Select
                  isSearchable={false}
                  placeholder={t('placeholders.selectCurrency')}
                  closeMenuOnSelect
                  options={currencies}
                  defaultValue={currencies.find(x => x.label === config.currency)}
                  onChange={(e) => setCurrency({ value: e.value, label: e.label })}
                  classNamePrefix="rs"
                />
              </div>
            </div>

            <div className="infoActions mt-3">
              <CustomButton
                disabled={currencyButtonLoading}
                onClick={SubmitCurrency}
                className="app-btn--sm"
                label={t('actions.save')}
                loading={currencyButtonLoading}
              />
            </div>
          </section>
        </div>
      ) : !config.detailsSubmitted ? (
        //I think here we could have a cancel button disconnect stripe here if they want to and check for requiremts stage too
        <div>
          <div className={`payConnect ${!hasOnlinePaymentsAccess ? 'paySectionLocked' : ''}`}>
            <div className="payConnect__card">
              {!hasOnlinePaymentsAccess && <LockedBadge />}

              <div className="payConnect__info">
                <div className="payConnect__badge">{t('badge.payments')}</div>
                <h3 className="payConnect__title">{t('titles.connectStripe')}</h3>
                <p className="payConnect__sub">
                  {t('subtitles.enableOnlinePayments')}
                </p>
              </div>

<div className="payConnect__actions">
  <button
    type="button"
    className="payConnect__btn"
    onClick={ConnectStripAccountButtonClick}
    disabled={connectStripeAccountLoading || disconnectAccountButtonLoading || !hasOnlinePaymentsAccess}
  >
    <span className="payConnect__btnIcon" aria-hidden="true">
      <FaStripe />
    </span>
    {!connectStripeAccountLoading ? (
      <span>{t('actions.finishStripeSetup')}</span>
    ) : (
      <span className="app-btn__spinner" />
    )}
  </button>

  <button
    type="button"
    className="payConnect__subtleBtn"
    onClick={DisconnectAccountClicked}
    disabled={disconnectAccountButtonLoading || connectStripeAccountLoading}
  >
    {disconnectAccountButtonLoading ? (
      <span className="app-btn__spinner" />
    ) : (
      <span>{t('actions.cancelStripeSetup')}</span>
    )}
  </button>
</div>
            </div>
          </div>

          <section className="schedCard mt-4">
            <header className="schedHead">
              <h3 className="schedTitle">{t('titles.currency')}</h3>
              <p className="schedDesc">{t('subtitles.currencyDescription')}</p>
            </header>
            <div className="schedBody">
              <div className="schedSelect">
                <Select
                  isSearchable={false}
                  placeholder={t('placeholders.selectCurrency')}
                  closeMenuOnSelect
                  options={currencies}
                  defaultValue={currencies.find(x => x.label === config.currency)}
                  onChange={(e) => setCurrency({ value: e.value, label: e.label })}
                  classNamePrefix="rs"
                />
              </div>
            </div>
            <div className="infoActions mt-3">
              <CustomButton
                disabled={currencyButtonLoading}
                onClick={SubmitCurrency}
                className="app-btn--sm"
                label={t('actions.save')}
                loading={currencyButtonLoading}
              />
            </div>
          </section>
        </div>
      ) : paymentServiceDetails.requirements ? (
        <div>
          <div className={`payConnect ${!hasOnlinePaymentsAccess ? 'paySectionLocked' : ''}`}>
            <div className="payConnect__card">
              {!hasOnlinePaymentsAccess && <LockedBadge />}

              <div className="payConnect__info">
                <div className="payConnect__badge">{t('badge.payments')}</div>
                <h3 className="payConnect__title">{t('titles.connectStripe')}</h3>
                <p className="payConnect__sub" style={{ color:"#3c1c1c" }}>
                  {t('subtitles.requirementsAttention')}
                </p>
              </div>

<div className="payConnect__actions">
  <button
    type="button"
    className="payConnect__btn"
    style={{ backgroundColor: "#b63939" }}
    onClick={ConnectStripAccountAttentionButtonClick}
    disabled={connectStripeAccountLoading || disconnectAccountButtonLoading || !hasOnlinePaymentsAccess}
  >
    <span className="payConnect__btnIcon" aria-hidden="true">
      <FaStripe />
    </span>
    {!connectStripeAccountLoading ? (
      <span>{t('actions.accountRequiresAttention')}</span>
    ) : (
      <span className="app-btn__spinner" />
    )}
  </button>

  <button
    type="button"
    className="payConnect__subtleBtn"
    onClick={DisconnectAccountClicked}
    disabled={disconnectAccountButtonLoading || connectStripeAccountLoading}
  >
    {disconnectAccountButtonLoading ? (
      <span className="app-btn__spinner" />
    ) : (
      <span>{t('actions.cancelStripeSetup')}</span>
    )}
  </button>
</div>
            </div>
          </div>

          <section className="schedCard mt-4">
            <header className="schedHead">
              <h3 className="schedTitle">{t('titles.currency')}</h3>
              <p className="schedDesc">{t('subtitles.currencyDescription')}</p>
            </header>
            <div className="schedBody">
              <div className="schedSelect">
                <Select
                  isSearchable={false}
                  placeholder={t('placeholders.selectCurrency')}
                  closeMenuOnSelect
                  options={currencies}
                  defaultValue={currencies.find(x => x.label === config.currency)}
                  onChange={(e) => setCurrency({ value: e.value, label: e.label })}
                  classNamePrefix="rs"
                />
              </div>
            </div>
            <div className="infoActions mt-3">
              <CustomButton
                disabled={currencyButtonLoading}
                onClick={SubmitCurrency}
                className="app-btn--sm"
                label={t('actions.save')}
                loading={currencyButtonLoading}
              />
            </div>
          </section>
        </div>
      ) : (
        <div className="payDash">
          {!hasOnlinePaymentsAccess && (
            <div className="payAccessBanner">
              <div className="payAccessBanner__textWrap">
                <div className="payAccessBanner__title">{t('titles.paymentsPaused')}</div>
                <div className="payAccessBanner__text">{t('subtitles.paymentsPaused')}</div>
              </div>
              <div className="payAccessBanner__lockWrap">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="stripe-payments-paused-tooltip">
                      {t('tooltips.locked')}
                    </Tooltip>
                  }
                >
                  <span className="payAccessBanner__lock" aria-label={t('tooltips.locked')}>
                    <IoLockClosed />
                  </span>
                </OverlayTrigger>
              </div>
            </div>
          )}

          <div className="payDash__head">
            <div style={{display:"flex", justifyContent: "space-between"}}>
              {hasOnlinePaymentsAccess ?

              <div className={`payDash__status ${status.toLowerCase().replace(" ", "-")}`}>
                {statusText}
              </div>
              : null}
              {hasOnlinePaymentsAccess ?
              <div>
                <button
                  type="button"
                  className="icon-refresh-btn"
                  onClick={RefreshBalanceWrapper}
                  aria-label={t('actions.refreshBalances')}
                  title={t('actions.refreshBalances')}
                >
                  <RefreshIcon fontSize="small" />
                </button>
              </div>
              :
              null
              }

            </div>
            {hasOnlinePaymentsAccess ?
            <div className="payDash__titleRow" style={{display:"flex", justifyContent:"end"}}>
              <div className="payDash__toggle">
                <label className="payDash__toggleLabel">{t('actions.acceptOnlinePayments')}</label>
                <div className="live-switch-wrapper">
                  <input
                    className="toggle-switch"
                    type="checkbox"
                    checked={enabledOnlinePaymentsButton}
                    id="accept-payments"
                    onChange={updateAcceptPaymentsOption}
                    disabled={!hasOnlinePaymentsAccess}
                  />
                  <label htmlFor="accept-payments" className="switch-label-payments" />
                </div>
              </div>
            </div>
            : null}
          </div>

          
          {hasOnlinePaymentsAccess ?
          <div>
                    <div className="payDash__stats">
            <div className="payDash__statCard">
              <span className="payDash__statLabel">{t('labels.availableFunds')}</span>
              <div className="payDash__statValue">
                {formatMinor(paymentServiceDetails.availableFunds, config.currency)}
              </div>
              <div className="subtext">{t('labels.availableFundsDescription')}</div>
            </div>
            <div className="payDash__statCard">
              <span className="payDash__statLabel">{t('labels.pendingFunds')}</span>
              <div className="payDash__statValue">
                {formatMinor(paymentServiceDetails.pendingFunds, config.currency)}
                
              </div>
              <div className="subtext">{t('labels.pendingFundDescription')}</div>
            </div>
          </div>
                    <div className="payDash__divider" />

          <section className="payDash__section">
            <h3 className="payDash__h3">{t('titles.stripeAccount')}</h3>
            <div className="payDash__actions">
              <button
                className="payDash__btn payDash__btn--primary"
                onClick={ManageStripeAccountClicked}
                disabled={manageAccountButtonLoading || !hasOnlinePaymentsAccess}
              >
                {!manageAccountButtonLoading ? (
                  t('actions.manageAccount')
                ) : (
                  <span className="app-btn__spinner" />
                )}
              </button>

              <button
                className="payDash__btn payDash__btn--danger"
                onClick={DisconnectAccountClicked}
                disabled={disconnectAccountButtonLoading || !hasOnlinePaymentsAccess}
              >
                {disconnectAccountButtonLoading ? (
                  <span className="app-btn__spinner" />
                ) : (
                  t('actions.disconnectAccount')
                )}
              </button>
            </div>
          </section>
          <div className="payDash__divider" />
          </div>
          : 
                    <section className="payDash__section">
            <h3 className="payDash__h3">{t('titles.stripeAccount')}</h3>
            <div className="payDash__actions">

              <button
                className="payDash__btn payDash__btn--danger"
                onClick={DisconnectAccountClicked}
                disabled={disconnectAccountButtonLoading}
              >
                {disconnectAccountButtonLoading ? (
                  <span className="app-btn__spinner" />
                ) : (
                  t('actions.disconnectAccount')
                )}
              </button>
            </div>
            <div className="payDash__divider" />
          </section>
          }

          




          

          <header className="schedHead">
            <h3 className="schedTitle">{t('titles.currency')}</h3>
            <p className="schedDesc">{t('subtitles.currencyDescription')}</p>
          </header>

          <div className="schedBody">
            <div className="schedSelect">
              <Select
                isSearchable={false}
                placeholder={t('placeholders.selectCurrency')}
                closeMenuOnSelect
                options={currencies}
                defaultValue={currencies.find(x => x.label === config.currency)}
                onChange={(e) => setCurrency({ value: e.value, label: e.label })}
                classNamePrefix="rs"
              />
            </div>
            <p className="sm-hint" style={{fontSize:"0.9rem"}}>
              {t('subtitles.balancesHint')}
            </p>
          </div>

          <div className="infoActions mt-3">
            <CustomButton
              disabled={currencyButtonLoading}
              onClick={SubmitCurrency}
              className="app-btn--sm"
              label={t('actions.save')}
              loading={currencyButtonLoading}
            />
          </div>

          <div className="payDash__divider" />

          <section className="payDash__help">
            <h3 className="payDash__h3">{t('titles.needHelp')}</h3>
            <ul className="payDash__links">
              <li>
                <a
                  href="https://stripe.com/docs/connect/express-dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('links.whatIsStripeConnect')}
                </a>
              </li>
              <li>
                <a href="/support">{t('links.contactSupport')}</a>
              </li>
            </ul>
          </section>
        </div>
      )}
      </>
      : null}
    </>
  );
};

export default Payments;